import { useState } from 'react';
import {
  Button,
  Modal,
  Paragraph,
  Stack,
  Spinner,
} from '@contentful/f36-components';
import { config } from '../config/app-config';
import useMsalToken from '../hooks/useMsalToken';
import ErrorModal from './ErrorModal';

const handlePushToFTA = async (entryId, sdk, setIsLoading, setIsModalOpen, accessToken, setErrorMessage, setIsErrorModalOpen, reauthenticate) => {
  if (!accessToken) {
    sdk.notifier.error('Authentication token not available. Please wait and try again.');
    return;
  }

  setIsLoading(true);

  try {
    const environment = sdk.parameters.installation.environment || 'std';
    const apiUrl = config[environment]?.api || config.std.api;
    const response = await fetch(`${apiUrl}/content/migration/article`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ entryId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      if (response.status === 401 && errorData.code === 'INACTIVITY_TIMEOUT') {
        sdk.notifier.warning('Session expired due to inactivity. Please re-authenticate.');
        setIsModalOpen(false);
        await reauthenticate();
        return;
      }
      throw new Error(errorData.error || errorData.message || 'Failed to initiate migration');
    }

    sdk.notifier.success('Migration finished successfully.');
    setIsModalOpen(false);
  } catch (error) {
    sdk.notifier.error(
      error.message || 'An error occurred while pushing to FTA.'
    );
    setErrorMessage(error.message || 'An error occurred while pushing to FTA.');
    setIsErrorModalOpen(true);
    setIsModalOpen(false);
  } finally {
    setIsLoading(false);
  }
};

const ArticleMigration = ({ sdk, entryId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { accessToken, reauthenticate } = useMsalToken();

  return (
    <Stack flexDirection="column" alignItems='center' justifyContent='center' style={{ height: '100vh' }}>
      <Button
        variant="primary"
        onClick={() => setIsModalOpen(true)}
        isDisabled={isLoading || !accessToken}
        startIcon={isLoading ? <Spinner size="small" /> : null}
      >
        {!accessToken ? 'Authenticating...' : isLoading ? 'Migrating...' : 'Migrate'}
      </Button>

      <Modal
        onClose={() => setIsModalOpen(false)}
        isShown={isModalOpen}
        title="Confirm Migration"
        size="fullscreen"
      >
        {() => (
          <>
            <Modal.Content style={{ padding: '0.5rem' }}>
              <Paragraph marginBottom="none" style={{ textAlign: 'center' }} fontSize="fontSizeS">
                Are you sure you want to push this Article to FIFA+? This will overwrite existing content.
              </Paragraph>
            </Modal.Content>
            <Modal.Controls>
              <Button
                variant="transparent"
                onClick={() => setIsModalOpen(false)}
                size='small'
                isDisabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="positive"
                onClick={() => handlePushToFTA(entryId, sdk, setIsLoading, setIsModalOpen, accessToken, setErrorMessage, setIsErrorModalOpen, reauthenticate)}
                size='small'
                isDisabled={isLoading || !accessToken}
                isLoading={isLoading}
              >
                {!accessToken ? 'Authenticating...' : isLoading ? 'Pushing' : 'Confirm'}
              </Button>
            </Modal.Controls>
          </>
        )}
      </Modal>

      <ErrorModal
        isShown={isErrorModalOpen}
        errorMessage={errorMessage}
        onClose={() => setIsErrorModalOpen(false)}
      />
    </Stack>
  );
};

export default ArticleMigration;
